import type { AxiosRequestConfig } from 'axios';
import { http } from './client';

/**
 * Thin promise-of-T wrapper over the shared axios client. App-side endpoint
 * maps (e.g. `Tms.getApiInfo`) build on top of these.
 */
export const request = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    http.get<T>(url, config).then((r) => r.data),
  post: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
    http.post<T>(url, body, config).then((r) => r.data),
  put: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
    http.put<T>(url, body, config).then((r) => r.data),
  del: <T>(url: string, config?: AxiosRequestConfig) =>
    http.delete<T>(url, config).then((r) => r.data),

  /**
   * Multipart POST helper for endpoints that take an `IFormFile` (logo
   * upload, document upload, etc.). Builds a FormData from the supplied
   * record, lets the browser set the multipart boundary, and forwards
   * everything else (auth, X-App-Key, X-Idempotency-Key) through the same
   * interceptor pipeline as `request.post`.
   *
   * `fields` accepts strings, Blobs, and Files. Numbers/booleans are
   * stringified — matches `[FromForm]` model binding semantics on the
   * server. `null`/`undefined` values are skipped so callers can spread an
   * options object without writing a filter step.
   */
  postMultipart: <T>(
    url: string,
    fields: Record<string, string | number | boolean | Blob | File | null | undefined>,
    config?: AxiosRequestConfig,
  ) => {
    const form = new FormData();
    for (const [key, value] of Object.entries(fields)) {
      if (value === null || value === undefined) continue;
      if (value instanceof Blob) {
        form.append(key, value, value instanceof File ? value.name : undefined);
      } else {
        form.append(key, String(value));
      }
    }
    return http.post<T>(url, form, config).then((r) => r.data);
  },
};
